import React, { useState } from 'react';
import { useBooking } from '../../contexts/booking/BookingContext';
import { useAuth } from '../../contexts/auth/AuthContext';
import { supabase } from '../../lib/supabaseClient';
import { format, parseISO, addMinutes } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import {
  getNumericPrice,
  getNumericDuration,
  getTotalDuration,
  formatPrice,
  calculatePricingWithCredits,
  PAY_NOW_DISCOUNT_PERCENT
} from '../../utils/bookingUtils';
import CreditSelectionSection from './CreditSelectionSection';

/**
 * Component for reviewing booking details before proceeding to payment.
 * Appointment creation is now handled in the PaymentForm component.
 * In edit mode, handles updating the existing appointment via RPC.
 */
const BookingSummary: React.FC = () => {
  const { bookingState, prevStep, nextStep, isEditMode, goToStep, setAppointmentId } = useBooking();
  const { isMember, userProfile, memberCredits } = useAuth();
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateError, setUpdateError] = useState<string | null>(null);
  const { selectedServices, selectedStaff, selectedDate, selectedTime, customerInfo, creditsToUse } = bookingState;

  // Calculate pricing with member credits and discount
  const walletBalance = userProfile?.wallet_balance || 0;
  const pricing = calculatePricingWithCredits(
    selectedServices,
    isMember,
    walletBalance,
    memberCredits,
    creditsToUse
  );

  // Validate required data
  if (!selectedDate || !selectedTime || !customerInfo) {
    return <div>Missing required booking information. Please go back and complete all steps.</div>;
  }

  if (!selectedTime) {
    return <div>Error: Booking time not selected. Please go back.</div>;
  }

  // Parse the stored UTC time and convert to Texas timezone for display
  const utcDateTime = parseISO(selectedTime);
  const appointmentDateTime = toZonedTime(utcDateTime, 'America/Chicago');
  const totalDuration = getTotalDuration(selectedServices);
  const appointmentEndTime = addMinutes(appointmentDateTime, totalDuration);

  /**
   * Proceeds to the payment step.
   * Appointment creation is handled in PaymentForm to prevent duplicates.
   */
  const handleContinueToPayment = () => {
    nextStep();
  };

  /**
   * Updates an existing appointment in edit mode via RPC.
   */
  const handleUpdateAppointment = async () => {
    if (!bookingState.editingAppointmentId || !selectedTime) return;

    setIsUpdating(true);
    setUpdateError(null);

    try {
      // Build services JSONB array
      const servicesPayload = selectedServices.map(svc => ({
        service_id: svc.id,
        price: getNumericPrice(svc),
        duration: getNumericDuration(svc),
      }));

      // Parse the selected time to get start/end timestamps
      const startTimeISO = selectedTime;
      // Remove trailing Z for timestamp without timezone
      const startTimeClean = startTimeISO.replace('Z', '').replace('T', ' ');
      const startDate = parseISO(startTimeISO);
      const endDate = addMinutes(startDate, totalDuration);
      const endTimeClean = endDate.toISOString().replace('Z', '').replace('T', ' ');

      const { data, error } = await supabase.rpc('update_appointment', {
        p_appointment_id: bookingState.editingAppointmentId,
        p_staff_id: selectedStaff?.id || null,
        p_start_time: startTimeClean,
        p_end_time: endTimeClean,
        p_notes: customerInfo?.notes || null,
        p_services: servicesPayload,
      });

      if (error) {
        if (error.message?.includes('TIME_SLOT_CONFLICT')) {
          setUpdateError('This time slot is no longer available. Please choose a different time.');
        } else if (error.message?.includes('Cannot edit')) {
          setUpdateError('This appointment can no longer be edited.');
        } else {
          setUpdateError(error.message || 'Failed to update appointment. Please try again.');
        }
        return;
      }

      // Success - go to confirmation step
      setAppointmentId(bookingState.editingAppointmentId);
      goToStep(7);
    } catch (err: any) {
      setUpdateError(err.message || 'An unexpected error occurred.');
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div>
      <h2 className="text-2xl font-serif text-[#232347] mb-2">Review Your Booking</h2>
      <p className="text-gray-600 mb-6">Please review your appointment details before proceeding to payment</p>

      <div className="bg-[#F1F7F4] p-6 rounded-md mb-6">
        <h3 className="text-xl font-serif text-[#232347] mb-4">Appointment Summary</h3>

        {/* Date & Time */}
        <div className="mb-4">
          <h4 className="font-medium text-[#232347] mb-2">Date & Time</h4>
          <div className="flex items-center space-x-2">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-[#B2D8C5]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <span className="text-gray-700">
              {format(appointmentDateTime, 'EEEE, MMMM d, yyyy')} at {format(appointmentDateTime, 'h:mm a')} - {format(appointmentEndTime, 'h:mm a')}
            </span>
          </div>
        </div>

        {/* Staff */}
        <div className="mb-4">
          <h4 className="font-medium text-[#232347] mb-2">Therapist</h4>
          <div className="flex items-center space-x-2">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-[#B2D8C5]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            <span className="text-gray-700">
              {selectedStaff ? selectedStaff.name : 'No preference (best available therapist)'}
            </span>
          </div>
        </div>

        {/* Free Service Credits Selection */}
        <CreditSelectionSection />

        {/* Services */}
        <div className="mb-4">
          <h4 className="font-medium text-[#232347] mb-2">Services</h4>
          <div className="space-y-2">
            {selectedServices.map((service, index) => {
              // Check if this service is covered by credits
              const creditForService = pricing.creditsApplied.find(c => c.serviceId === service.id);
              const isFree = creditForService && creditForService.quantity > 0;
              // Count how many of this service have been marked as free
              const freeCountForService = creditForService?.quantity || 0;
              const servicesOfThisType = selectedServices.filter(s => s.id === service.id);
              const thisServiceIndex = servicesOfThisType.slice(0, servicesOfThisType.indexOf(service) + 1).length;
              const isThisOneFree = isFree && thisServiceIndex <= freeCountForService;

              return (
                <div key={`${service.id}-${index}`} className="flex justify-between items-center text-gray-700 pl-7">
                  <span className="flex items-center gap-2">
                    {service.name} ({getNumericDuration(service)} min)
                    {isThisOneFree && (
                      <span className="px-2 py-0.5 bg-teal-500 text-white text-xs font-bold rounded">
                        FREE
                      </span>
                    )}
                  </span>
                  <span className={isThisOneFree ? 'line-through text-gray-400' : ''}>
                    ${formatPrice(getNumericPrice(service))}
                  </span>
                </div>
              );
            })}

            {/* Subtotal */}
            <div className="border-t border-gray-200 mt-2 pt-2 flex justify-between text-gray-700 pl-7">
              <span>Subtotal</span>
              <span>${formatPrice(pricing.subtotal)}</span>
            </div>

            {/* Credit Savings (if applicable) */}
            {pricing.totalCreditSavings > 0 && (
              <div className="flex justify-between text-teal-600 pl-7">
                <span className="flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Free Service Credits ({pricing.freeServicesCount})
                </span>
                <span>-${formatPrice(pricing.totalCreditSavings)}</span>
              </div>
            )}

            {/* Member Discount (if applicable) */}
            {pricing.memberDiscountAmount > 0 && (
              <div className="flex justify-between text-green-600 pl-7">
                <span className="flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Member Discount ({pricing.memberDiscountPercent}%)
                </span>
                <span>-${formatPrice(pricing.memberDiscountAmount)}</span>
              </div>
            )}

            {/* Total */}
            <div className="border-t border-gray-200 mt-2 pt-2 flex justify-between font-medium text-[#232347] pl-7">
              <span>Total</span>
              <span>${formatPrice(pricing.afterDiscount)}</span>
            </div>

            {/* Pay Now Discount Banner (for non-members) */}
            {!isMember && (
              <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-md pl-7">
                <div className="flex items-center text-green-700">
                  <span className="text-lg mr-2">✨</span>
                  <div>
                    <p className="font-medium">Save {PAY_NOW_DISCOUNT_PERCENT}% when you pay online!</p>
                    <p className="text-sm text-green-600 mt-0.5">
                      Pay during checkout instead of at the spa and save ${formatPrice(pricing.afterDiscount * PAY_NOW_DISCOUNT_PERCENT / 100)}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Wallet Balance Info (if user has wallet funds) */}
            {walletBalance > 0 && (
              <div className="mt-3 p-3 bg-blue-50 rounded-md pl-7">
                <div className="flex justify-between text-blue-700">
                  <span className="flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                    </svg>
                    Wallet Balance
                  </span>
                  <span>${formatPrice(walletBalance)}</span>
                </div>
                {pricing.canPayWithWallet && (
                  <p className="text-sm text-blue-600 mt-1">
                    You can pay with your wallet balance on the next step.
                  </p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Customer Info */}
        <div>
          <h4 className="font-medium text-[#232347] mb-2">Your Information</h4>
          <div className="space-y-2 pl-7">
            <div className="text-gray-700">
              {customerInfo.firstName} {customerInfo.lastName}
            </div>
            <div className="text-gray-700">
              Email: {customerInfo.email}
            </div>
            <div className="text-gray-700">
              Phone: {customerInfo.phone}
            </div>
            {customerInfo.notes && (
              <div className="text-gray-700">
                <p className="font-medium">Notes:</p>
                <p>{customerInfo.notes}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Cancellation Policy */}
      <div className="mb-6 bg-white border border-gray-200 p-4 rounded-md">
        <h3 className="font-medium text-[#232347] mb-2">Cancellation Policy</h3>
        <p className="text-gray-600 text-sm">
          Please provide at least 24 hours notice for cancellations or rescheduling to avoid a cancellation fee of 50% of the service price.
          No-shows will be charged the full service price.
        </p>
      </div>

      {/* Update error message */}
      {updateError && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
          <p className="text-red-700 text-sm">{updateError}</p>
        </div>
      )}

      {/* Navigation buttons */}
      <div className="mt-8 flex justify-between">
        <button
          onClick={prevStep}
          className="px-6 py-2 rounded-md border border-[#B2D8C5] text-[#232347] hover:bg-[#F1F7F4] transition-colors"
        >
          Back
        </button>
        {isEditMode ? (
          <button
            onClick={handleUpdateAppointment}
            disabled={isUpdating}
            className="px-6 py-2 rounded-md text-white bg-[#B2D8C5] hover:bg-[#88bfa3] transition-colors disabled:opacity-50"
          >
            {isUpdating ? 'Updating...' : 'Update Appointment'}
          </button>
        ) : (
          <button
            onClick={handleContinueToPayment}
            className="px-6 py-2 rounded-md text-white bg-[#B2D8C5] hover:bg-[#88bfa3] transition-colors"
          >
            Continue to Payment
          </button>
        )}
      </div>
    </div>
  );
};

export default BookingSummary;
